import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { format } from "date-fns";
import { Loader2, ShieldAlert, Plus, Filter } from "lucide-react";

interface Incident {
  id: string;
  title: string;
  description: string;
  severity: "P1" | "P2" | "P3" | "P4";
  status: "open" | "contained" | "eradicated" | "recovered" | "closed";
  affected_data_categories: string[];
  estimated_affected_subjects: number | null;
  detected_at: string;
  contained_at: string | null;
  closed_at: string | null;
  anpd_notification_required: boolean;
  anpd_notified_at: string | null;
  anpd_notification_protocol: string | null;
  reported_by: string;
  created_at: string;
}

interface IncidentEvent {
  id: string;
  event_type: string;
  payload: Record<string, unknown>;
  actor_user_id: string | null;
  created_at: string;
}

const SEVERITY_LABELS: Record<string, string> = {
  P1: "P1 — Crítico",
  P2: "P2 — Alto",
  P3: "P3 — Médio",
  P4: "P4 — Baixo",
};

const STATUS_LABELS: Record<string, string> = {
  open: "Aberto",
  contained: "Contido",
  eradicated: "Erradicado",
  recovered: "Recuperado",
  closed: "Fechado",
};

const STATUS_OPTIONS = ["open", "contained", "eradicated", "recovered", "closed"];

const DATA_CATEGORIES = [
  { value: "personal", label: "Dados pessoais" },
  { value: "sensitive_health", label: "Dados sensíveis (saúde)" },
  { value: "credentials", label: "Credenciais / autenticação" },
  { value: "financial", label: "Dados financeiros" },
  { value: "location", label: "Geolocalização" },
  { value: "minors", label: "Dados de menores" },
];

function severityVariant(s: string): "destructive" | "default" | "secondary" | "outline" {
  if (s === "P1") return "destructive";
  if (s === "P2") return "default";
  if (s === "P3") return "secondary";
  return "outline";
}

function statusVariant(s: string): "default" | "destructive" | "secondary" | "outline" {
  if (s === "closed") return "default";
  if (s === "open") return "destructive";
  return "secondary";
}

export function IncidentsTab() {
  const { user } = useAuth();
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("open");
  const [severityFilter, setSeverityFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newIncident, setNewIncident] = useState({
    title: "",
    description: "",
    severity: "P3" as Incident["severity"],
    affected_data_categories: [] as string[],
    estimated_affected_subjects: "",
    anpd_notification_required: false,
  });

  const [selected, setSelected] = useState<Incident | null>(null);
  const [events, setEvents] = useState<IncidentEvent[]>([]);
  const [statusDraft, setStatusDraft] = useState<string>("");
  const [protocolDraft, setProtocolDraft] = useState("");
  const [markAnpd, setMarkAnpd] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase
      .from("security_incidents" as any)
      .select("*")
      .order("detected_at", { ascending: false });
    setIncidents(((data as unknown) as Incident[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { void load(); }, [load]);

  const loadEvents = useCallback(async (id: string) => {
    const { data } = await supabase
      .from("security_incident_events" as any)
      .select("*")
      .eq("incident_id", id)
      .order("created_at", { ascending: false });
    setEvents(((data as unknown) as IncidentEvent[]) ?? []);
  }, []);

  const openDetails = (i: Incident) => {
    setSelected(i);
    setStatusDraft(i.status);
    setProtocolDraft(i.anpd_notification_protocol ?? "");
    setMarkAnpd(false);
    void loadEvents(i.id);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return incidents.filter((i) => {
      if (statusFilter !== "all" && i.status !== statusFilter) return false;
      if (severityFilter !== "all" && i.severity !== severityFilter) return false;
      if (!q) return true;
      return i.title.toLowerCase().includes(q) || i.description.toLowerCase().includes(q);
    });
  }, [incidents, statusFilter, severityFilter, search]);

  const metrics = useMemo(() => {
    const open = incidents.filter((i) => i.status !== "closed").length;
    const critical = incidents.filter((i) => i.severity === "P1" && i.status !== "closed").length;
    const anpdPending = incidents.filter((i) => i.anpd_notification_required && !i.anpd_notified_at).length;
    return { open, critical, anpdPending, total: incidents.length };
  }, [incidents]);

  const handleCreate = async () => {
    if (!user) return;
    if (!newIncident.title.trim() || !newIncident.description.trim()) {
      toast.error("Preencha título e descrição");
      return;
    }
    setCreating(true);
    const { error } = await supabase.from("security_incidents" as any).insert({
      title: newIncident.title.trim(),
      description: newIncident.description.trim(),
      severity: newIncident.severity,
      affected_data_categories: newIncident.affected_data_categories,
      estimated_affected_subjects: newIncident.estimated_affected_subjects
        ? Number(newIncident.estimated_affected_subjects)
        : null,
      anpd_notification_required: newIncident.anpd_notification_required,
      reported_by: user.id,
    });
    setCreating(false);
    if (error) {
      toast.error(`Falha: ${error.message}`);
      return;
    }
    toast.success("Incidente registrado");
    setCreateOpen(false);
    setNewIncident({
      title: "",
      description: "",
      severity: "P3",
      affected_data_categories: [],
      estimated_affected_subjects: "",
      anpd_notification_required: false,
    });
    await load();
  };

  const handleSaveChanges = async () => {
    if (!selected) return;
    setSaving(true);
    const updates: Partial<Incident> = {};
    if (statusDraft !== selected.status) updates.status = statusDraft as Incident["status"];
    if (protocolDraft !== (selected.anpd_notification_protocol ?? "")) {
      updates.anpd_notification_protocol = protocolDraft || null;
    }
    if (markAnpd && !selected.anpd_notified_at) {
      (updates as any).anpd_notified_at = new Date().toISOString();
    }

    if (Object.keys(updates).length === 0) {
      toast.info("Nenhuma alteração");
      setSaving(false);
      return;
    }

    const { error } = await supabase
      .from("security_incidents" as any)
      .update(updates)
      .eq("id", selected.id);

    setSaving(false);
    if (error) {
      toast.error(`Falha: ${error.message}`);
      return;
    }
    toast.success("Atualizado");
    await load();
    await loadEvents(selected.id);
    setSelected({ ...selected, ...updates } as Incident);
    setMarkAnpd(false);
  };

  const toggleCategory = (cat: string) => {
    setNewIncident((prev) => ({
      ...prev,
      affected_data_categories: prev.affected_data_categories.includes(cat)
        ? prev.affected_data_categories.filter((c) => c !== cat)
        : [...prev.affected_data_categories, cat],
    }));
  };

  return (
    <div className="space-y-4">
      {/* Métricas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MetricCard label="Em aberto" value={metrics.open} />
        <MetricCard label="P1 críticos" value={metrics.critical} tone="destructive" />
        <MetricCard label="ANPD pendente" value={metrics.anpdPending} tone="warn" />
        <MetricCard label="Total" value={metrics.total} />
      </div>

      {/* Filtros + ação */}
      <Card>
        <CardContent className="pt-4 flex flex-col sm:flex-row gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos status</SelectItem>
                {STATUS_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Select value={severityFilter} onValueChange={setSeverityFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas severidades</SelectItem>
              {Object.keys(SEVERITY_LABELS).map((s) => (
                <SelectItem key={s} value={s}>{SEVERITY_LABELS[s]}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input
            placeholder="Buscar por título ou descrição..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 min-w-48"
          />
          <Button onClick={() => setCreateOpen(true)} className="gap-2">
            <Plus className="w-4 h-4" /> Registrar incidente
          </Button>
        </CardContent>
      </Card>

      {/* Lista */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldAlert className="w-4 h-4" /> Incidentes ({filtered.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <Skeleton className="h-32 w-full" />
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nenhum incidente encontrado.</p>
          ) : (
            <ul className="divide-y">
              {filtered.map((i) => (
                <li key={i.id}>
                  <button
                    type="button"
                    onClick={() => openDetails(i)}
                    className="w-full text-left py-3 flex flex-wrap items-center gap-3 hover:bg-muted/40 rounded px-2 -mx-2"
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium truncate">{i.title}</p>
                      <p className="text-xs text-muted-foreground">
                        Detectado em {format(new Date(i.detected_at), "dd/MM/yyyy HH:mm")}
                      </p>
                    </div>
                    <Badge variant={severityVariant(i.severity)}>{i.severity}</Badge>
                    <Badge variant={statusVariant(i.status)}>{STATUS_LABELS[i.status]}</Badge>
                    {i.anpd_notification_required && !i.anpd_notified_at && (
                      <Badge variant="destructive" className="text-xs">ANPD pendente</Badge>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Modal criação */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registrar incidente de segurança</DialogTitle>
            <DialogDescription>
              Conforme LGPD art. 48, incidentes que possam acarretar risco aos titulares devem ser comunicados à ANPD.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid gap-1.5">
              <Label>Título *</Label>
              <Input
                value={newIncident.title}
                onChange={(e) => setNewIncident({ ...newIncident, title: e.target.value })}
                placeholder="Ex: Acesso indevido a base de pacientes"
              />
            </div>
            <div className="grid gap-1.5">
              <Label>Descrição *</Label>
              <Textarea
                rows={4}
                value={newIncident.description}
                onChange={(e) => setNewIncident({ ...newIncident, description: e.target.value })}
                placeholder="Descreva natureza, vetor, sistemas afetados, ações iniciais..."
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Severidade *</Label>
                <Select
                  value={newIncident.severity}
                  onValueChange={(v) => setNewIncident({ ...newIncident, severity: v as Incident["severity"] })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SEVERITY_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Titulares afetados (estimado)</Label>
                <Input
                  type="number"
                  min={0}
                  value={newIncident.estimated_affected_subjects}
                  onChange={(e) => setNewIncident({ ...newIncident, estimated_affected_subjects: e.target.value })}
                  placeholder="Ex: 150"
                />
              </div>
            </div>
            <div className="grid gap-1.5">
              <Label>Categorias de dados afetados</Label>
              <div className="grid grid-cols-2 gap-2">
                {DATA_CATEGORIES.map((c) => (
                  <label key={c.value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <Checkbox
                      checked={newIncident.affected_data_categories.includes(c.value)}
                      onCheckedChange={() => toggleCategory(c.value)}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between border-t pt-3">
              <div>
                <Label className="font-medium">Notificação à ANPD necessária</Label>
                <p className="text-xs text-muted-foreground">Marque se o incidente exige comunicação à ANPD.</p>
              </div>
              <Switch
                checked={newIncident.anpd_notification_required}
                onCheckedChange={(v) => setNewIncident({ ...newIncident, anpd_notification_required: v })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={creating}>
              {creating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Registrar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal detalhe */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.title}</DialogTitle>
                <DialogDescription>
                  ID {selected.id.slice(0, 8)} · Detectado em {format(new Date(selected.detected_at), "dd/MM/yyyy HH:mm")}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="flex flex-wrap gap-2">
                  <Badge variant={severityVariant(selected.severity)}>{SEVERITY_LABELS[selected.severity]}</Badge>
                  <Badge variant={statusVariant(selected.status)}>{STATUS_LABELS[selected.status]}</Badge>
                  {selected.anpd_notification_required && (
                    <Badge variant={selected.anpd_notified_at ? "default" : "destructive"}>
                      {selected.anpd_notified_at ? "ANPD notificada" : "ANPD pendente"}
                    </Badge>
                  )}
                </div>

                <div>
                  <Label className="text-xs text-muted-foreground">Descrição</Label>
                  <p className="whitespace-pre-wrap">{selected.description}</p>
                </div>

                {selected.affected_data_categories.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Categorias afetadas</Label>
                    <div className="flex flex-wrap gap-1 mt-1">
                      {selected.affected_data_categories.map((c) => (
                        <Badge key={c} variant="outline">
                          {DATA_CATEGORIES.find((d) => d.value === c)?.label ?? c}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {selected.estimated_affected_subjects !== null && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Titulares afetados (estimado)</Label>
                    <p>{selected.estimated_affected_subjects.toLocaleString("pt-BR")}</p>
                  </div>
                )}

                <div className="border-t pt-3 space-y-3">
                  <div className="grid gap-1.5">
                    <Label>Atualizar status</Label>
                    <Select value={statusDraft} onValueChange={setStatusDraft}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map((s) => (
                          <SelectItem key={s} value={s}>{STATUS_LABELS[s]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {selected.anpd_notification_required && (
                    <>
                      <div className="grid gap-1.5">
                        <Label>Protocolo ANPD</Label>
                        <Input
                          value={protocolDraft}
                          onChange={(e) => setProtocolDraft(e.target.value)}
                          placeholder="Nº do protocolo da comunicação"
                        />
                      </div>
                      {!selected.anpd_notified_at && (
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox checked={markAnpd} onCheckedChange={(v) => setMarkAnpd(!!v)} />
                          Marcar ANPD como notificada agora
                        </label>
                      )}
                      {selected.anpd_notified_at && (
                        <p className="text-xs text-muted-foreground">
                          ANPD notificada em {format(new Date(selected.anpd_notified_at), "dd/MM/yyyy HH:mm")}
                        </p>
                      )}
                    </>
                  )}

                  <Button size="sm" onClick={handleSaveChanges} disabled={saving}>
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Salvar alterações
                  </Button>
                </div>

                <div className="border-t pt-3">
                  <Label className="mb-2 block">Trilha de eventos</Label>
                  {events.length === 0 ? (
                    <p className="text-xs text-muted-foreground">Nenhum evento.</p>
                  ) : (
                    <ul className="space-y-2 max-h-60 overflow-y-auto">
                      {events.map((ev) => (
                        <li key={ev.id} className="text-xs border-l-2 border-muted pl-2">
                          <p className="font-medium">{ev.event_type}</p>
                          <p className="text-muted-foreground">
                            {format(new Date(ev.created_at), "dd/MM/yyyy HH:mm")}
                          </p>
                          {Object.keys(ev.payload ?? {}).length > 0 && (
                            <pre className="text-[10px] bg-muted p-1 rounded mt-1 overflow-x-auto">
                              {JSON.stringify(ev.payload, null, 2)}
                            </pre>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Fechar</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MetricCard({ label, value, tone }: { label: string; value: number; tone?: "destructive" | "warn" }) {
  const color =
    tone === "destructive"
      ? "text-destructive"
      : tone === "warn"
      ? "text-warning"
      : "text-foreground";
  return (
    <Card>
      <CardContent className="pt-4 pb-3">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className={`text-2xl font-bold ${color}`}>{value}</p>
      </CardContent>
    </Card>
  );
}
